// services/UnifiedCheatingDetectionService.js
import { db } from './firebase';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';

/**
 * Cheating detection via a simple boolean field on the room document.
 * - Candidate: sets rooms/{meetingId}.tabSwitched = true on tab hide
 * - Interviewer: listens to that field and fires a callback when true
 */
class TabSwitchDetectionService {
    constructor() {
        this.meetingId = null;
        this.userRole = null;

        // Candidate-side
        this.isMonitoring = false;
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

        // Interviewer-side
        this.unsubscribe = null;
        this.onTabSwitchCallback = null;
    }

    initialize(meetingId, userRole) {
        this.meetingId = meetingId;
        this.userRole = userRole;

        if (userRole === 'candidate') {
            // Small delay so the page settling doesn't fire a false positive
            setTimeout(() => this.startMonitoring(), 3000);
        } else if (userRole === 'interviewer') {
            this.startListening();
        }

        return this;
    }

    // ─── CANDIDATE ────────────────────────────────────────────────────────────

    startMonitoring() {
        if (this.isMonitoring) return;
        this.isMonitoring = true;
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    handleVisibilityChange() {
        if (!this.isMonitoring || !this.meetingId) return;

        if (document.hidden) {
            // Tab lost focus → flag it
            this._setTabSwitched(true);
        }
        // We intentionally do NOT clear the flag when the candidate comes back;
        // the interviewer's modal dismiss does that (or you can clear it here if preferred).
    }

    async _setTabSwitched(value) {
        try {
            const roomRef = doc(db, 'rooms', this.meetingId);
            await updateDoc(roomRef, { tabSwitched: value });
        } catch (err) {
            console.error('CheatingDetectionService: failed to update tabSwitched', err);
        }
    }

    stopMonitoring() {
        if (!this.isMonitoring) return;
        this.isMonitoring = false;
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    // ─── INTERVIEWER ──────────────────────────────────────────────────────────

    /**
     * @param {(isSwitched: boolean) => void} callback
     */
    onTabSwitch(callback) {
        this.onTabSwitchCallback = callback;
        return this;
    }

    startListening() {
        if (!this.meetingId) return;
        const roomRef = doc(db, 'rooms', this.meetingId);

        this.unsubscribe = onSnapshot(roomRef, (snapshot) => {
            const data = snapshot.data();
            if (data && typeof data.tabSwitched === 'boolean') {
                this.onTabSwitchCallback?.(data.tabSwitched);
            }
        });
    }

    /**
     * Called by the interviewer when they dismiss the alert modal.
     * Clears the flag so it can fire again on the next tab switch.
     */
    async acknowledgeTabSwitch() {
        await this._setTabSwitched(false);
    }

    stopListening() {
        this.unsubscribe?.();
        this.unsubscribe = null;
    }

    // ─── SHARED ───────────────────────────────────────────────────────────────

    cleanup() {
        this.stopMonitoring();
        this.stopListening();
        this.onTabSwitchCallback = null;
    }
}

export default TabSwitchDetectionService;